import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, Package, TrendingUp, TrendingDown, DollarSign, CheckCircle,
  AlertCircle, Loader2, History, Target, ArrowRight, Scale, Zap
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ── Types ───────────────────────────────────────────────────────────────────

interface SalesOrder {
  id: string;
  number: string;
  status: string;
  total: number;
  items: { description: string; quantity: number; rate: number }[];
  account: { name: string } | null;
  config: Record<string, any>;
}

interface VendorQuote {
  id: string;
  vendorId: string;
  vendorName: string;
  commodity: string;
  rate: number;
  quantity: number;
  total: number;
  deliveryDays: number;
  qualityGrade: string;
  createdAt: string;
  history: { date: string; rate: number }[];
}

interface LogisticsQuote {
  id: string;
  transporterId: string;
  transporterName: string;
  routeCost: number;
  vehicleType: string;
  etaDays: number;
  route: string;
  createdAt: string;
  history: { date: string; cost: number }[];
}

interface SelectedCombo {
  vendorId: string | null;
  vendorName: string;
  transporterId: string | null;
  transporterName: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function marginColor(pct: number): string {
  if (pct >= 15) return 'bg-green-100 text-green-800 border-green-300';
  if (pct >= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-red-100 text-red-800 border-red-300';
}

function marginBadgeColor(pct: number): string {
  if (pct >= 15) return 'bg-green-600';
  if (pct >= 5) return 'bg-yellow-500';
  return 'bg-red-600';
}

// ── Component ───────────────────────────────────────────────────────────────

export function LandedCostPage() {
  const navigate = useNavigate();

  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [selectedSoId, setSelectedSoId] = useState('');
  const [vendorQuotes, setVendorQuotes] = useState<VendorQuote[]>([]);
  const [logisticsQuotes, setLogisticsQuotes] = useState<LogisticsQuote[]>([]);
  const [selectedCombo, setSelectedCombo] = useState<SelectedCombo>({
    vendorId: null, vendorName: '', transporterId: null, transporterName: '',
  });

  const [loadingSOs, setLoadingSOs] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showVendorHistory, setShowVendorHistory] = useState<string | null>(null);
  const [showLogisticsHistory, setShowLogisticsHistory] = useState<string | null>(null);

  // ── Fetch SOs ─────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/sales-orders?status=confirmed&limit=100');
        const data = res.data.data?.salesOrders ?? res.data.data ?? [];
        setSalesOrders(data);
        if (data.length === 1) setSelectedSoId(data[0].id);
      } catch (err) {
        console.error('Failed to fetch SOs', err);
      } finally {
        setLoadingSOs(false);
      }
    })();
  }, []);

  // ── Fetch quotes when SO changes ──────────────────────────────────────

  useEffect(() => {
    if (!selectedSoId) {
      setVendorQuotes([]);
      setLogisticsQuotes([]);
      return;
    }
    (async () => {
      setLoadingQuotes(true);
      try {
        const [vRes, lRes, soRes] = await Promise.all([
          api.get('/procurement/vendor-quotes').catch(() => ({ data: { data: [] } })),
          api.get(`/logistics/quotes?soId=${selectedSoId}`).catch(() => ({ data: { data: [] } })),
          api.get(`/sales-orders/${selectedSoId}`),
        ]);

        const vData: VendorQuote[] = (vRes.data.data || []).map((q: any) => ({
          id: q.id,
          vendorId: q.vendorId,
          vendorName: q.vendorName || q.vendor?.name || 'Unknown Vendor',
          commodity: q.commodity,
          rate: Number(q.rate) || 0,
          quantity: Number(q.quantity) || 0,
          total: (Number(q.rate) || 0) * (Number(q.quantity) || 0),
          deliveryDays: Number(q.deliveryDays) || 0,
          qualityGrade: q.qualityGrade || 'N/A',
          createdAt: q.createdAt,
          history: q.history || [],
        }));

        const lData: LogisticsQuote[] = (lRes.data.data || []).map((q: any) => ({
          id: q.id,
          transporterId: q.transporterId || q.transportPartnerId,
          transporterName: q.transporterName || q.transportPartner?.name || 'Unknown Transporter',
          routeCost: Number(q.routeCost) || Number(q.cost) || Number(q.rate) || 0,
          vehicleType: q.vehicleType || 'Standard',
          etaDays: Number(q.etaDays) || Number(q.eta) || 0,
          route: q.route || q.description || '',
          createdAt: q.createdAt,
          history: q.history || [],
        }));

        setVendorQuotes(vData);
        setLogisticsQuotes(lData);

        // Populate currently selected combo from SO config
        const so = soRes.data.data;
        const config = so?.config || {};
        setSelectedCombo({
          vendorId: config.selectedVendorId || null,
          vendorName: config.selectedVendorName || '',
          transporterId: config.selectedTransporterId || null,
          transporterName: config.selectedTransporterName || '',
        });
      } catch (err) {
        console.error('Failed to fetch quotes', err);
      } finally {
        setLoadingQuotes(false);
      }
    })();
  }, [selectedSoId]);

  // ── Derived data ──────────────────────────────────────────────────────

  const so = salesOrders.find(s => s.id === selectedSoId);
  const quantity = so?.items?.[0]?.quantity ?? 0;
  const sellRate = so?.items?.[0]?.rate ?? 0;
  const sellTotal = Number(so?.total) || 0;

  // Build the combined comparison matrix
  const matrix = useMemo(() => {
    if (!vendorQuotes.length || !logisticsQuotes.length) return null;

    let bestCost = Infinity;
    let bestVendorId = '';
    let bestLogisticsId = '';

    const cells: {
      vendorId: string;
      logisticsId: string;
      landedCost: number;
      margin: number;
      marginPct: number;
    }[][] = [];

    for (const vq of vendorQuotes) {
      const row: typeof cells[0] = [];
      for (const lq of logisticsQuotes) {
        const commodityCost = vq.rate * quantity;
        const landedCost = commodityCost + lq.routeCost;
        const margin = sellTotal - landedCost;
        const marginPct = sellTotal > 0 ? (margin / sellTotal) * 100 : 0;

        if (landedCost < bestCost) {
          bestCost = landedCost;
          bestVendorId = vq.id;
          bestLogisticsId = lq.id;
        }

        row.push({ vendorId: vq.id, logisticsId: lq.id, landedCost, margin, marginPct });
      }
      cells.push(row);
    }

    return { cells, bestVendorId, bestLogisticsId, bestCost };
  }, [vendorQuotes, logisticsQuotes, quantity, sellTotal]);

  // Current combo cost
  const currentComboCost = useMemo(() => {
    if (!selectedCombo.vendorId || !selectedCombo.transporterId) return null;
    const vq = vendorQuotes.find(v => v.vendorId === selectedCombo.vendorId || v.id === selectedCombo.vendorId);
    const lq = logisticsQuotes.find(l => l.transporterId === selectedCombo.transporterId || l.id === selectedCombo.transporterId);
    if (!vq || !lq) return null;
    return (vq.rate * quantity) + lq.routeCost;
  }, [vendorQuotes, logisticsQuotes, selectedCombo, quantity]);

  const potentialSavings = (currentComboCost && matrix) ? currentComboCost - matrix.bestCost : 0;

  // ── Actions ───────────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!selectedSoId) return;
    setApproving(true);
    try {
      await api.post(`/sales-orders/${selectedSoId}/financial-approval`, {
        type: 'landed_cost',
        approved: true,
        notes: `Approved with landed cost analysis. Best combo: ${matrix?.bestCost ? formatINR(matrix.bestCost) : 'N/A'}`,
      });
      alert('Combination approved successfully');
      navigate(`/sales-orders/${selectedSoId}`);
    } catch (err) {
      alert('Failed to approve combination');
    } finally {
      setApproving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loadingSOs) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading sales orders...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Landed Cost Comparison</h1>
        <p className="text-sm text-gray-500">
          Vendor + Logistics combined analysis — select the combination with lowest total landed cost
        </p>
        <div className="mt-1 flex items-center gap-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <AlertCircle size={14} className="shrink-0" />
          <span className="font-medium">Business Rule:</span>
          <span>Expensive Vendor + Cheap Logistics can beat Cheap Vendor + Expensive Logistics. Always compare TOTAL landed cost.</span>
        </div>
      </div>

      {/* Sales Order Selector */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <label className="mb-2 block text-sm font-semibold text-gray-700">Select Sales Order</label>
        <select
          value={selectedSoId}
          onChange={e => setSelectedSoId(e.target.value)}
          className="w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">-- Select a confirmed Sales Order --</option>
          {salesOrders.map(s => (
            <option key={s.id} value={s.id}>
              {s.number} — {s.account?.name || 'N/A'} — {s.items?.[0]?.description || 'N/A'} ({formatINR(s.total)})
            </option>
          ))}
        </select>

        {so && (
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div>
              <p className="text-xs text-gray-500">Buyer</p>
              <p className="font-medium text-gray-900 text-sm">{so.account?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Commodity</p>
              <p className="font-medium text-gray-900 text-sm">{so.items?.[0]?.description || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Quantity (MT)</p>
              <p className="font-medium text-gray-900 text-sm">{quantity}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sell Rate</p>
              <p className="font-medium text-gray-900 text-sm">{formatINR(sellRate)}/MT</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Value</p>
              <p className="font-medium text-gray-900 text-sm">{formatINR(sellTotal)}</p>
            </div>
          </div>
        )}
      </div>

      {loadingQuotes && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading quotes...</span>
        </div>
      )}

      {selectedSoId && !loadingQuotes && (
        <>
          {/* Stats Cards */}
          {matrix && (
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <Target size={16} /> Best Combo Cost
                </div>
                <p className="text-2xl font-bold text-green-700">{formatINR(matrix.bestCost)}</p>
                <p className="text-xs text-green-600 mt-1">
                  Margin: {formatINR(sellTotal - matrix.bestCost)} ({sellTotal > 0 ? ((sellTotal - matrix.bestCost) / sellTotal * 100).toFixed(1) : '0'}%)
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <DollarSign size={16} /> Current Combo Cost
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {currentComboCost ? formatINR(currentComboCost) : 'Not Selected'}
                </p>
                {selectedCombo.vendorName && (
                  <p className="text-xs text-gray-500 mt-1">{selectedCombo.vendorName} + {selectedCombo.transporterName}</p>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Zap size={16} /> Potential Savings
                </div>
                <p className={`text-2xl font-bold ${potentialSavings > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                  {potentialSavings > 0 ? formatINR(potentialSavings) : '—'}
                </p>
                {potentialSavings > 0 && (
                  <p className="text-xs text-green-600 mt-1">Switch to optimal combo to save</p>
                )}
              </div>
            </div>
          )}

          {/* Vendor Quotes Table */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 border-b">
              <Package size={18} className="text-gray-500" />
              <h3 className="font-semibold text-gray-900">Vendor Quotes</h3>
              <span className="text-sm text-gray-500">({vendorQuotes.length} vendors)</span>
            </div>
            {vendorQuotes.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400">No vendor quotes found for this SO</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      <th className="px-4 py-2 font-medium text-gray-500">#</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Vendor</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Rate/MT</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Total Cost</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Delivery</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Quality</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Trend</th>
                      <th className="px-4 py-2 font-medium text-gray-500">History</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[...vendorQuotes].sort((a, b) => a.rate - b.rate).map((q, idx) => (
                      <tr key={q.id} className={idx === 0 ? 'bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-2">
                          {idx === 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Best</span>
                          ) : (
                            <span className="text-gray-400">#{idx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">{q.vendorName}</td>
                        <td className="px-4 py-2 font-semibold text-gray-900">{formatINR(q.rate)}</td>
                        <td className="px-4 py-2 text-gray-700">{formatINR(q.rate * quantity)}</td>
                        <td className="px-4 py-2 text-gray-600">{q.deliveryDays} days</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            q.qualityGrade === 'A' ? 'bg-green-100 text-green-700' :
                            q.qualityGrade === 'B' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{q.qualityGrade}</span>
                        </td>
                        <td className="px-4 py-2">
                          {q.history && q.history.length > 1 && (() => {
                            const first = q.history[0].rate;
                            const last = q.history[q.history.length - 1].rate;
                            const trend = ((last - first) / first) * 100;
                            return (
                              <span className={`flex items-center gap-1 text-xs ${trend < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {trend < 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                                {Math.abs(trend).toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => setShowVendorHistory(showVendorHistory === q.id ? null : q.id)}
                            className="text-[var(--ink-0)] hover:text-[var(--ink-0)] text-xs flex items-center gap-1"
                          >
                            <History size={12} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vendor price history expanded */}
            {showVendorHistory && (() => {
              const q = vendorQuotes.find(v => v.id === showVendorHistory);
              if (!q || !q.history?.length) return null;
              return (
                <div className="p-4 bg-gray-50 border-t">
                  <h4 className="text-xs font-medium text-gray-600 mb-2">Price History — {q.vendorName}</h4>
                  <div className="flex items-end gap-1 h-20">
                    {q.history.map((h, i) => {
                      const maxRate = Math.max(...q.history.map(x => x.rate));
                      const height = maxRate > 0 ? (h.rate / maxRate) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 bg-[var(--saffron-100)] rounded-t transition-all" style={{ height: `${height}%` }} title={`${h.date}: ${formatINR(h.rate)}`} />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{q.history[0]?.date || 'Oldest'}</span>
                    <span>{q.history[q.history.length - 1]?.date || 'Latest'}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Logistics Quotes Table */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 border-b">
              <Truck size={18} className="text-gray-500" />
              <h3 className="font-semibold text-gray-900">Logistics Quotes</h3>
              <span className="text-sm text-gray-500">({logisticsQuotes.length} transporters)</span>
            </div>
            {logisticsQuotes.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400">No logistics quotes found for this SO</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      <th className="px-4 py-2 font-medium text-gray-500">#</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Transporter</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Route Cost</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Vehicle Type</th>
                      <th className="px-4 py-2 font-medium text-gray-500">ETA</th>
                      <th className="px-4 py-2 font-medium text-gray-500">Route</th>
                      <th className="px-4 py-2 font-medium text-gray-500">History</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[...logisticsQuotes].sort((a, b) => a.routeCost - b.routeCost).map((q, idx) => (
                      <tr key={q.id} className={idx === 0 ? 'bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-2">
                          {idx === 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Cheapest</span>
                          ) : (
                            <span className="text-gray-400">#{idx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">{q.transporterName}</td>
                        <td className="px-4 py-2 font-semibold text-gray-900">{formatINR(q.routeCost)}</td>
                        <td className="px-4 py-2 text-gray-600">{q.vehicleType}</td>
                        <td className="px-4 py-2 text-gray-600">{q.etaDays} days</td>
                        <td className="px-4 py-2 text-gray-500 text-xs max-w-[160px] truncate">{q.route || '—'}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => setShowLogisticsHistory(showLogisticsHistory === q.id ? null : q.id)}
                            className="text-[var(--ink-0)] hover:text-[var(--ink-0)] text-xs flex items-center gap-1"
                          >
                            <History size={12} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Logistics history expanded */}
            {showLogisticsHistory && (() => {
              const q = logisticsQuotes.find(l => l.id === showLogisticsHistory);
              if (!q || !q.history?.length) return null;
              return (
                <div className="p-4 bg-gray-50 border-t">
                  <h4 className="text-xs font-medium text-gray-600 mb-2">Cost History — {q.transporterName}</h4>
                  <div className="flex items-end gap-1 h-20">
                    {q.history.map((h, i) => {
                      const maxCost = Math.max(...q.history.map(x => x.cost));
                      const height = maxCost > 0 ? (h.cost / maxCost) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 bg-orange-200 rounded-t transition-all" style={{ height: `${height}%` }} title={`${h.date}: ${formatINR(h.cost)}`} />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{q.history[0]?.date || 'Oldest'}</span>
                    <span>{q.history[q.history.length - 1]?.date || 'Latest'}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ──────────────────────────────────────────────────────────────── */}
          {/* COMBINED COMPARISON MATRIX — the key feature                    */}
          {/* ──────────────────────────────────────────────────────────────── */}
          {matrix && vendorQuotes.length > 0 && logisticsQuotes.length > 0 && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 border-b">
                <Scale size={18} className="text-gray-500" />
                <h3 className="font-semibold text-gray-900">Combined Comparison Matrix</h3>
                <span className="ml-auto flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-300" /> &ge;15% margin</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-yellow-100 border border-yellow-300" /> 5-15% margin</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" /> &lt;5% margin</span>
                </span>
              </div>

              <div className="overflow-x-auto p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 bg-gray-50 rounded-tl-lg">
                        Vendor \ Transporter
                      </th>
                      {logisticsQuotes.map(lq => (
                        <th key={lq.id} className="px-3 py-2 text-center font-medium text-gray-700 bg-gray-50 min-w-[140px]">
                          <div className="flex flex-col items-center gap-0.5">
                            <span>{lq.transporterName}</span>
                            <span className="text-xs text-gray-400 font-normal">{formatINR(lq.routeCost)}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vendorQuotes.map((vq, rowIdx) => (
                      <tr key={vq.id}>
                        <td className="px-3 py-2 font-medium text-gray-900 bg-gray-50 border-r border-gray-100">
                          <div>
                            <span>{vq.vendorName}</span>
                            <span className="block text-xs text-gray-400 font-normal">{formatINR(vq.rate)}/MT</span>
                          </div>
                        </td>
                        {matrix.cells[rowIdx].map((cell, colIdx) => {
                          const isBest = cell.vendorId === matrix.bestVendorId && cell.logisticsId === matrix.bestLogisticsId;
                          const colorClass = marginColor(cell.marginPct);
                          return (
                            <td
                              key={`${cell.vendorId}-${cell.logisticsId}`}
                              className={`px-3 py-2 text-center border border-gray-100 relative ${colorClass} ${isBest ? 'ring-2 ring-green-600 ring-inset' : ''}`}
                            >
                              {isBest && (
                                <span className="absolute top-1 right-1 inline-flex items-center rounded-full bg-green-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                  BEST
                                </span>
                              )}
                              <p className="font-bold text-sm">{formatINR(cell.landedCost)}</p>
                              <p className="text-xs mt-0.5">
                                Margin: {formatINR(cell.margin)}
                              </p>
                              <p className="text-xs">
                                ({cell.marginPct.toFixed(1)}%)
                              </p>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Selected Combination + Approve */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Selected Combination</h2>
            {selectedCombo.vendorName || selectedCombo.transporterName ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500 mb-1">Selected Vendor</p>
                  <p className="font-medium text-gray-900">{selectedCombo.vendorName || '—'}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500 mb-1">Selected Transporter</p>
                  <p className="font-medium text-gray-900">{selectedCombo.transporterName || '—'}</p>
                </div>
              </div>
            ) : (
              <div className="mb-4 rounded-lg bg-gray-50 p-4 text-center text-gray-400 text-sm">
                No vendor + transporter combination selected yet for this deal
              </div>
            )}

            {/* Cost comparison if both exist */}
            {currentComboCost && matrix && (
              <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Current vs Best</p>
                    <p className="text-xs text-blue-600">
                      Current: {formatINR(currentComboCost)} <ArrowRight size={10} className="inline mx-1" /> Best: {formatINR(matrix.bestCost)}
                    </p>
                  </div>
                  {potentialSavings > 0 && (
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-700">{formatINR(potentialSavings)}</p>
                      <p className="text-xs text-green-600">potential savings</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Back
              </button>
              <button
                onClick={handleApprove}
                disabled={approving}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--ink-0)] text-white rounded-lg text-sm hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {approving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Approve Combination
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty state when no SO selected */}
      {!selectedSoId && !loadingSOs && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Scale size={48} className="mb-3" />
          <p className="text-sm">Select a confirmed Sales Order above to view landed cost comparison</p>
        </div>
      )}
    </div>
  );
}
